/**
 * Texture Utilities - Tools for inspecting and applying textures to models
 * 
 * GLB/GLTF files can have textures in several ways:
 * 1. Embedded (binary data inside the GLB file)
 * 2. External (referenced by relative path)
 * 3. None (materials without textures)
 * 
 * This utility helps diagnose texture issues and apply external textures.
 */

import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Material } from "@babylonjs/core/Materials/material";
import { Color3 } from "@babylonjs/core/Maths/math.color";

/**
 * Information about a material in the model
 */
export interface MaterialInfo {
    name: string;
    type: string;
    meshes: string[];
    hasAlbedoTexture: boolean;
    hasNormalTexture: boolean;
    hasMetallicTexture: boolean;
    hasEmissiveTexture: boolean;
    hasOcclusionTexture: boolean;
    albedoColor?: { r: number; g: number; b: number };
    alpha: number;
}

/**
 * Information about textures in the model
 */
export interface ModelTextureInfo {
    materials: MaterialInfo[];
    totalMeshes: number;
    meshesWithMaterial: number;
    meshesWithTextures: number;
    summary: string;
}

/**
 * Texture configuration for applying to a model
 */
export interface TextureConfig {
    /** Diffuse/Albedo texture path */
    diffuse?: string;
    /** Normal map texture path */
    normal?: string;
    /** Metallic/Roughness texture path (PBR) */
    metallicRoughness?: string;
    /** Ambient occlusion texture path */
    occlusion?: string;
    /** Emissive texture path */
    emissive?: string;
    /** Apply to specific materials by name (null = all materials) */
    materialNames?: string[];
}

/**
 * Utilities for working with textures on GLB models
 */
export class TextureUtils {
    
    /**
     * Analyze all materials and textures in a model
     * Use this to understand what textures a GLB has (or is missing)
     */
    public static analyzeModelTextures(meshes: AbstractMesh[]): ModelTextureInfo {
        const materialMap = new Map<Material, MaterialInfo>();
        let meshesWithMaterial = 0;
        let meshesWithTextures = 0;

        meshes.forEach(mesh => {
            const material = mesh.material;
            if (!material) return;

            meshesWithMaterial++;

            if (!materialMap.has(material)) {
                const info = this._analyzeMaterial(material);
                materialMap.set(material, info);
            }

            const info = materialMap.get(material)!;
            info.meshes.push(mesh.name);

            if (info.hasAlbedoTexture || info.hasNormalTexture) {
                meshesWithTextures++;
            }
        });

        const materials = Array.from(materialMap.values());
        const hasTextures = materials.some(m => 
            m.hasAlbedoTexture || m.hasNormalTexture || m.hasMetallicTexture
        );

        let summary = "";
        if (materials.length === 0) {
            summary = "⚠️ No materials found on model";
        } else if (!hasTextures) {
            summary = "⚠️ Model has materials but NO textures - textures may need to be applied manually";
        } else {
            summary = `✓ Model has ${materials.length} material(s) with textures`;
        }

        return {
            materials,
            totalMeshes: meshes.length,
            meshesWithMaterial,
            meshesWithTextures,
            summary,
        };
    }

    /**
     * Analyze a single material
     */
    private static _analyzeMaterial(material: Material): MaterialInfo {
        const info: MaterialInfo = {
            name: material.name,
            type: material.getClassName(),
            meshes: [],
            hasAlbedoTexture: false,
            hasNormalTexture: false,
            hasMetallicTexture: false,
            hasEmissiveTexture: false,
            hasOcclusionTexture: false,
            alpha: material.alpha,
        };

        if (material instanceof PBRMaterial) {
            info.hasAlbedoTexture = !!material.albedoTexture;
            info.hasNormalTexture = !!material.bumpTexture;
            info.hasMetallicTexture = !!material.metallicTexture;
            info.hasEmissiveTexture = !!material.emissiveTexture;
            info.hasOcclusionTexture = !!material.ambientTexture;
            if (material.albedoColor) {
                info.albedoColor = {
                    r: material.albedoColor.r,
                    g: material.albedoColor.g,
                    b: material.albedoColor.b,
                };
            }
        } else if (material instanceof StandardMaterial) {
            info.hasAlbedoTexture = !!material.diffuseTexture;
            info.hasNormalTexture = !!material.bumpTexture;
            info.hasEmissiveTexture = !!material.emissiveTexture;
            if (material.diffuseColor) {
                info.albedoColor = {
                    r: material.diffuseColor.r,
                    g: material.diffuseColor.g,
                    b: material.diffuseColor.b,
                };
            }
        }

        return info;
    }

    /**
     * Print texture analysis to console (useful for debugging)
     */
    public static printTextureAnalysis(meshes: AbstractMesh[]): void {
        const analysis = this.analyzeModelTextures(meshes);
        
        console.log("=== Model Texture Analysis ===");
        console.log(analysis.summary);
        console.log(`Total meshes: ${analysis.totalMeshes}`);
        console.log(`Meshes with materials: ${analysis.meshesWithMaterial}`);
        console.log(`Meshes with textures: ${analysis.meshesWithTextures}`);
        console.log("");
        
        analysis.materials.forEach((mat, index) => {
            console.log(`Material ${index + 1}: "${mat.name}" (${mat.type})`);
            console.log(`  - Albedo/Diffuse texture: ${mat.hasAlbedoTexture ? "✓" : "✗"}`);
            console.log(`  - Normal texture: ${mat.hasNormalTexture ? "✓" : "✗"}`);
            console.log(`  - Metallic texture: ${mat.hasMetallicTexture ? "✓" : "✗"}`);
            console.log(`  - Emissive texture: ${mat.hasEmissiveTexture ? "✓" : "✗"}`);
            console.log(`  - Occlusion texture: ${mat.hasOcclusionTexture ? "✓" : "✗"}`);
            if (mat.albedoColor) {
                console.log(`  - Base color: RGB(${mat.albedoColor.r.toFixed(2)}, ${mat.albedoColor.g.toFixed(2)}, ${mat.albedoColor.b.toFixed(2)})`);
            }
            console.log(`  - Applied to: ${mat.meshes.join(", ")}`);
            console.log("");
        });
    }

    /**
     * Apply textures to a model's materials
     * 
     * @param meshes - The meshes to apply textures to
     * @param scene - The Babylon scene
     * @param config - Texture configuration
     * @param basePath - Base path for texture files (default: "assets/")
     */
    public static applyTextures(
        meshes: AbstractMesh[],
        scene: Scene,
        config: TextureConfig,
        basePath: string = "assets/"
    ): void {
        const processedMaterials = new Set<Material>();

        meshes.forEach(mesh => {
            const material = mesh.material;
            if (!material || processedMaterials.has(material)) return;

            // Check if we should process this material
            if (config.materialNames && config.materialNames.length > 0) {
                if (!config.materialNames.includes(material.name)) return;
            }

            processedMaterials.add(material);
            this._applyTexturesToMaterial(material, scene, config, basePath);
        });

        console.log(`TextureUtils: Applied textures to ${processedMaterials.size} material(s)`);
    }

    /**
     * Apply textures to a single material
     */
    private static _applyTexturesToMaterial(
        material: Material,
        scene: Scene,
        config: TextureConfig,
        basePath: string
    ): void {
        if (material instanceof PBRMaterial) {
            if (config.diffuse) {
                material.albedoTexture = new Texture(basePath + config.diffuse, scene);
                console.log(`  Applied albedo texture to "${material.name}"`);
            }
            if (config.normal) {
                material.bumpTexture = new Texture(basePath + config.normal, scene);
                console.log(`  Applied normal texture to "${material.name}"`);
            }
            if (config.metallicRoughness) {
                material.metallicTexture = new Texture(basePath + config.metallicRoughness, scene);
                material.useMetallnessFromMetallicTextureBlue = true;
                material.useRoughnessFromMetallicTextureGreen = true;
                console.log(`  Applied metallic/roughness texture to "${material.name}"`);
            }
            if (config.occlusion) {
                material.ambientTexture = new Texture(basePath + config.occlusion, scene);
                console.log(`  Applied occlusion texture to "${material.name}"`);
            }
            if (config.emissive) {
                material.emissiveTexture = new Texture(basePath + config.emissive, scene);
                console.log(`  Applied emissive texture to "${material.name}"`);
            }
        } else if (material instanceof StandardMaterial) {
            if (config.diffuse) {
                material.diffuseTexture = new Texture(basePath + config.diffuse, scene);
                console.log(`  Applied diffuse texture to "${material.name}"`);
            }
            if (config.normal) {
                material.bumpTexture = new Texture(basePath + config.normal, scene);
                console.log(`  Applied normal texture to "${material.name}"`);
            }
            if (config.emissive) {
                material.emissiveTexture = new Texture(basePath + config.emissive, scene);
                console.log(`  Applied emissive texture to "${material.name}"`);
            }
        }
    }

    /**
     * Create a new PBR material with textures and apply to meshes
     * Use this when you want to completely replace the model's materials
     */
    public static createAndApplyPBRMaterial(
        meshes: AbstractMesh[],
        scene: Scene,
        materialName: string,
        config: TextureConfig & {
            baseColor?: Color3;
            metallic?: number;
            roughness?: number;
        },
        basePath: string = "assets/"
    ): PBRMaterial {
        const material = new PBRMaterial(materialName, scene);

        // Set base properties
        if (config.baseColor) {
            material.albedoColor = config.baseColor;
        }
        material.metallic = config.metallic ?? 0;
        material.roughness = config.roughness ?? 1;

        // Apply textures
        if (config.diffuse) {
            material.albedoTexture = new Texture(basePath + config.diffuse, scene);
        }
        if (config.normal) {
            material.bumpTexture = new Texture(basePath + config.normal, scene);
        }
        if (config.metallicRoughness) {
            material.metallicTexture = new Texture(basePath + config.metallicRoughness, scene);
            material.useMetallnessFromMetallicTextureBlue = true;
            material.useRoughnessFromMetallicTextureGreen = true;
        }
        if (config.occlusion) {
            material.ambientTexture = new Texture(basePath + config.occlusion, scene);
        }
        if (config.emissive) {
            material.emissiveTexture = new Texture(basePath + config.emissive, scene);
        }

        // Apply to meshes
        meshes.forEach(mesh => {
            if (mesh.getTotalVertices() > 0) {
                mesh.material = material;
            }
        });

        console.log(`TextureUtils: Created and applied PBR material "${materialName}" to ${meshes.length} mesh(es)`);
        return material;
    }

    /**
     * Load a texture from file
     */
    public static loadTexture(
        scene: Scene,
        path: string,
        options?: {
            generateMipMaps?: boolean;
            invertY?: boolean;
            samplingMode?: number;
        }
    ): Texture {
        return new Texture(
            path,
            scene,
            !options?.generateMipMaps,  // noMipmap
            options?.invertY ?? true,
            options?.samplingMode
        );
    }

    /**
     * Get all unique materials from meshes
     */
    public static getMaterials(meshes: AbstractMesh[]): Material[] {
        const materials = new Set<Material>();
        meshes.forEach(mesh => {
            if (mesh.material) {
                materials.add(mesh.material);
            }
        });
        return Array.from(materials);
    }

    /**
     * Set material property on all meshes
     */
    public static setMaterialProperty(
        meshes: AbstractMesh[],
        property: keyof PBRMaterial | keyof StandardMaterial,
        value: unknown
    ): void {
        const materials = this.getMaterials(meshes);
        materials.forEach(mat => {
            if (property in mat) {
                (mat as unknown as Record<string, unknown>)[property] = value;
            }
        });
    }
}
